import { Box, ButtonBase, Typography, alpha } from "@mui/material";
import type { DateOnly } from "./dateOnly";
import {
	MONTH_NAMES,
	WEEKDAY_INITIALS,
	getMonthGrid,
	isDateAfter,
	isDateBefore,
	isSameMonth,
	parseDateOnly,
	getToday,
} from "./dateOnly";

const PICKER_CELL_SIZE = 36;

const isWithin = (span: DaySpan | null, date: DateOnly) =>
	span !== null &&
	!isDateBefore(date, span.from) &&
	!isDateAfter(date, span.to);

export interface DaySpan {
	from: DateOnly;
	to: DateOnly;
}

export interface RangeMonthProps {
	/** Any day of the month to render; only its year and month are read. */
	month: DateOnly;
	/** The days already chosen. Drawn as a solid band. */
	currentRange: DaySpan | null;
	/**
	 * The days the range would gain if the cursor were clicked. Drawn dashed, and kept apart
	 * from {@link currentRange} so an extension reads as an addition to what is already
	 * there rather than as a different range.
	 */
	previewRange: DaySpan | null;
	/** Days drawn as a filled circle: the ends of the chosen range. */
	edges: (DateOnly | null)[];
	/** Called for every cell of the grid. */
	isDateDisabled: (date: DateOnly) => boolean;
	onSelect: (date: DateOnly) => void;
	onHover: (date: DateOnly | null) => void;
}

export const RangeMonth = ({
	month,
	currentRange,
	previewRange,
	edges,
	isDateDisabled,
	onSelect,
	onHover,
}: RangeMonthProps) => {
	const parts = parseDateOnly(month);
	const today = getToday();

	return (
		<Box sx={{ width: PICKER_CELL_SIZE * 7 }}>
			<Typography
				sx={{
					fontSize: 15,
					fontWeight: 500,
					mb: 1,
					textAlign: "center",
				}}
			>
				{parts === null ? "" : `${MONTH_NAMES[parts.month - 1]} ${parts.year}`}
			</Typography>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: `repeat(7, ${PICKER_CELL_SIZE}px)`,
					rowGap: "4px",
				}}
			>
				{WEEKDAY_INITIALS.map((initial, index) => (
					<Typography
						key={index}
						sx={{
							color: "text.secondary",
							fontSize: 12,
							height: PICKER_CELL_SIZE,
							lineHeight: `${PICKER_CELL_SIZE}px`,
							textAlign: "center",
						}}
					>
						{initial}
					</Typography>
				))}
				{getMonthGrid(month).map((date, index) => {
					if (!isSameMonth(date, month)) {
						return <Box key={date} sx={{ height: PICKER_CELL_SIZE }} />;
					}

					const isDisabled = isDateDisabled(date);
					const isEdge = edges.includes(date);
					const isCurrent = isWithin(currentRange, date);
					const isPreview = isWithin(previewRange, date);
					const isBanded = isCurrent || isPreview;

					const column = index % 7;
					const span = isCurrent ? currentRange : previewRange;
					const isBandLeft = date === span?.from || column === 0;
					const isBandRight = date === span?.to || column === 6;

					return (
						<Box
							key={date}
							sx={(theme) => ({
								bgcolor: isCurrent
									? alpha(theme.palette.primary.main, 0.12)
									: "transparent",
								border: "1px dashed",
								borderColor: isPreview ? theme.palette.divider : "transparent",
								borderLeftColor:
									isPreview && isBandLeft
										? theme.palette.divider
										: "transparent",
								borderRightColor:
									isPreview && isBandRight
										? theme.palette.divider
										: "transparent",
								borderRadius: !isBanded
									? 0
									: `${isBandLeft ? "50%" : "0"} ${isBandRight ? "50%" : "0"} ${
											isBandRight ? "50%" : "0"
										} ${isBandLeft ? "50%" : "0"}`,
								boxSizing: "border-box",
								display: "flex",
								height: PICKER_CELL_SIZE,
								justifyContent: "center",
							})}
						>
							<ButtonBase
								disabled={isDisabled}
								onClick={() => onSelect(date)}
								onMouseEnter={() => onHover(date)}
								onMouseLeave={() => onHover(null)}
								sx={{
									bgcolor: isEdge ? "primary.main" : "transparent",
									border: date === today && !isEdge ? "1px solid" : "none",
									borderColor: "divider",
									borderRadius: "50%",
									color: isEdge
										? "primary.contrastText"
										: isDisabled
											? "text.disabled"
											: "text.primary",
									fontSize: 13,
									height: PICKER_CELL_SIZE - 2,
									width: PICKER_CELL_SIZE - 2,
									"&:hover": {
										bgcolor: isEdge ? "primary.dark" : "action.hover",
									},
									"&.Mui-disabled": { color: "text.disabled" },
								}}
							>
								{Number(date.slice(8, 10))}
							</ButtonBase>
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};
